import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Building2,
  Store,
  MapPin,
  Plus,
  Mail,
  Phone,
  Users as UsersIcon,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  CompanyFormDialog,
  CompanyTypeBadge,
  CompanyStatusBadge,
} from "@/pages/Companies";
import {
  EmployeeFormSheet,
  EmployeeStatusBadge,
  fullName,
  initials,
} from "@/components/EmployeeFormSheet";
import { SiteFormSheet } from "@/components/SiteFormSheet";


export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [sites, setSites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("sites");
  const [editCompanyOpen, setEditCompanyOpen] = useState(false);

  const [siteOpen, setSiteOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);

  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [defaultSiteForNewEmployee, setDefaultSiteForNewEmployee] = useState("");

  const loadData = async (showLoader = false) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const [companyRes, sitesRes, employeesRes] = await Promise.all([
        api.get(`/companies/${id}`),
        api.get(`/companies/${id}/sites`),
        api.get(`/companies/${id}/employees`),
      ]);
      setCompany(companyRes.data);
      setSites(sitesRes.data);
      setEmployees(employeesRes.data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Nem sikerült betölteni.");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refresh = () => loadData(false);

  const upsertSite = (saved) => {
    if (!saved) return;
    setSites((prev) => {
      const idx = prev.findIndex((s) => s._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev];
    });
  };

  const upsertEmployee = (saved) => {
    if (!saved) return;
    if (saved.company_id && saved.company_id !== id) return;
    setEmployees((prev) => {
      const idx = prev.findIndex((e) => e._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev];
    });
  };

  const onDeleteCompany = async () => {
    if (!window.confirm(`Biztosan törlöd "${company.name}" céget?`)) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Cég törölve.");
      navigate("/companies");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  const onDeleteSite = async (site) => {
    if (!window.confirm(`Biztosan törlöd "${site.name}" telephelyet?`)) return;
    try {
      await api.delete(`/sites/${site._id}`);
      toast.success("Telephely törölve.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  if (loading) {
    return (
      <div
        data-testid="company-detail-loading"
        className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground"
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex flex-col items-center gap-3 py-20 text-center">
        <p className="text-sm text-muted-foreground">{error || "Nem található."}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/companies">
            <ArrowLeft className="h-3.5 w-3.5" /> Vissza
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="company-detail-page" className="space-y-8">
      <div className="flex items-center gap-3">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 text-muted-foreground"
        >
          <Link to="/companies" data-testid="back-to-companies">
            <ArrowLeft className="h-3.5 w-3.5" /> Cégek
          </Link>
        </Button>
      </div>

      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-5">
          <div
            className={cn(
              "grid h-16 w-16 place-items-center rounded-2xl text-background",
              company.type === "franchise" ? "bg-violet-600" : "bg-blue-600"
            )}
          >
            {company.type === "franchise" ? (
              <Store className="h-7 w-7" strokeWidth={1.5} />
            ) : (
              <Building2 className="h-7 w-7" strokeWidth={1.5} />
            )}
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Cég adatlap
            </p>
            <h1
              data-testid="company-name"
              className="text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {company.name}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <CompanyTypeBadge type={company.type} />
              <CompanyStatusBadge value={company.status} />
              {company.tax_number && (
                <span className="font-mono text-[12px]">{company.tax_number}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setEditCompanyOpen(true)}
            data-testid="company-edit-button"
          >
            <Pencil className="h-4 w-4" /> Szerkesztés
          </Button>
          <Button
            variant="outline"
            onClick={onDeleteCompany}
            className="text-destructive hover:text-destructive"
            data-testid="company-delete-button"
          >
            <Trash2 className="h-4 w-4" /> Törlés
          </Button>
        </div>
      </div>

      {/* Company info */}
      <div className="grid gap-6 lg:grid-cols-3">
        <DetailCard title="Cégadatok" testId="card-company-data" className="lg:col-span-2">
          <DataGrid>
            <DataRow label="Adószám" value={company.tax_number} mono />
            <DataRow label="Cégjegyzékszám" value={company.registration_number} mono />
            <DataRow label="Kapcsolattartó" value={company.contact_person} />
            <DataRow
              label="E-mail"
              value={company.email}
              mono
              icon={<Mail className="h-3.5 w-3.5" strokeWidth={1.75} />}
            />
            <DataRow
              label="Telefon"
              value={company.phone}
              icon={<Phone className="h-3.5 w-3.5" strokeWidth={1.75} />}
            />
            <DataRow label="Cím" value={company.address} multiline />
          </DataGrid>
        </DetailCard>

        <DetailCard title="Összegzés" testId="card-summary">
          <DataGrid cols={1}>
            <DataRow
              label="Telephely"
              value={company.sites_count || 0}
              mono
              icon={<MapPin className="h-3.5 w-3.5" strokeWidth={1.75} />}
            />
            <DataRow
              label="Munkavállaló"
              value={company.employees_count || 0}
              mono
              icon={<UsersIcon className="h-3.5 w-3.5" strokeWidth={1.75} />}
            />
            {company.notes && (
              <DataRow label="Megjegyzés" value={company.notes} multiline />
            )}
          </DataGrid>
        </DetailCard>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList data-testid="company-detail-tabs">
          <TabsTrigger value="sites" data-testid="tab-sites">
            Telephelyek ({sites.length})
          </TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">
            Munkavállalók ({employees.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sites" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              A céghez tartozó telephelyek listája.
            </p>
            <Button
              size="sm"
              data-testid="new-site-button"
              onClick={() => {
                setEditingSite(null);
                setSiteOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Új telephely
            </Button>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
            {sites.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
                <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
                  <MapPin className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                </div>
                <p className="text-sm font-medium">Nincsenek telephelyek</p>
                <p className="text-xs text-muted-foreground">
                  Adj hozzá telephelyet a céghez.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 h-8"
                  onClick={() => {
                    setEditingSite(null);
                    setSiteOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" /> Új telephely
                </Button>
              </div>
            ) : (
              <table className="w-full min-w-[640px] text-sm" data-testid="sites-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                    <Th>Megnevezés</Th>
                    <Th>Cím</Th>
                    <Th>Vezető</Th>
                    <Th>Elérhetőség</Th>
                    <Th>Állapot</Th>
                    <Th className="pr-4 text-right">Műveletek</Th>
                  </tr>
                </thead>
                <tbody>
                  {sites.map((s) => (
                    <tr
                      key={s._id}
                      data-testid={`site-row-${s._id}`}
                      onClick={() => navigate(`/sites/${s._id}`)}
                      className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                    >
                      <Td>
                        <span className="font-medium">{s.name}</span>
                      </Td>
                      <Td className="text-muted-foreground">
                        {[s.postal_code, s.city, s.address]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </Td>
                      <Td className="text-muted-foreground">{s.manager_name || "—"}</Td>
                      <Td>
                        <div className="flex flex-col gap-0.5 text-[12px]">
                          {s.email && (
                            <span className="font-mono text-muted-foreground">{s.email}</span>
                          )}
                          {s.phone && (
                            <span className="text-muted-foreground">{s.phone}</span>
                          )}
                          {!s.email && !s.phone && (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <span
                          className={cn(
                            "inline-flex h-5 items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium",
                            s.status === "active"
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400"
                              : "border-border bg-muted text-muted-foreground"
                          )}
                        >
                          <span
                            className={cn(
                              "h-1.5 w-1.5 rounded-full",
                              s.status === "active"
                                ? "bg-emerald-500"
                                : "bg-muted-foreground/60"
                            )}
                          />
                          {s.status === "active" ? "Aktív" : "Inaktív"}
                        </span>
                      </Td>
                      <Td className="pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`site-actions-${s._id}`}
                              aria-label="Telephely műveletek"
                              className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              data-testid={`edit-site-${s._id}`}
                              onClick={() => {
                                setEditingSite(s);
                                setSiteOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                              Szerkesztés
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`add-employee-to-site-${s._id}`}
                              onClick={() => {
                                setDefaultSiteForNewEmployee(s._id);
                                setEmployeeOpen(true);
                              }}
                            >
                              <Plus className="h-4 w-4" />
                              Munkavállaló hozzáadása
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`delete-site-${s._id}`}
                              onClick={() => onDeleteSite(s)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                              Törlés
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="employees" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              A céghez rendelt munkavállalók.
            </p>
            <Button
              size="sm"
              data-testid="new-employee-at-company-button"
              onClick={() => {
                setDefaultSiteForNewEmployee("");
                setEmployeeOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Új munkavállaló
            </Button>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
            {employees.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
                <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
                  <UsersIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                </div>
                <p className="text-sm font-medium">Nincsenek munkavállalók</p>
                <p className="text-xs text-muted-foreground">
                  Adj hozzá munkavállalót ehhez a céghez.
                </p>
              </div>
            ) : (
              <table className="w-full min-w-[640px] text-sm" data-testid="company-employees-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                    <Th>Név</Th>
                    <Th>Beosztás</Th>
                    <Th>Telephely</Th>
                    <Th>Elérhetőség</Th>
                    <Th>Állapot</Th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => {
                    const site = sites.find((s) => s._id === emp.site_id);
                    return (
                      <tr
                        key={emp._id}
                        data-testid={`company-employee-row-${emp._id}`}
                        onClick={() => navigate(`/employees/${emp._id}`)}
                        className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                      >
                        <Td>
                          <div className="flex items-center gap-3">
                            <span className="grid h-7 w-7 place-items-center rounded-full bg-foreground/90 text-background text-[10px] font-semibold">
                              {initials(emp)}
                            </span>
                            <span className="font-medium">
                              {emp.titulus ? `${emp.titulus} ` : ""}
                              {fullName(emp)}
                            </span>
                          </div>
                        </Td>
                        <Td className="text-muted-foreground">{emp.position || "—"}</Td>
                        <Td className="text-muted-foreground">{site?.name || "—"}</Td>
                        <Td>
                          <div className="flex flex-col gap-0.5 text-[12px]">
                            {emp.email && (
                              <span className="font-mono text-muted-foreground">{emp.email}</span>
                            )}
                            {emp.phone && (
                              <span className="text-muted-foreground">{emp.phone}</span>
                            )}
                            {!emp.email && !emp.phone && (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        </Td>
                        <Td>
                          <EmployeeStatusBadge value={emp.status} />
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs / Sheets */}
      <CompanyFormDialog
        open={editCompanyOpen}
        onOpenChange={setEditCompanyOpen}
        company={company}
        onSaved={(saved) => {
          if (saved) setCompany((c) => ({ ...c, ...saved }));
          refresh();
        }}
      />
      <SiteFormSheet
        open={siteOpen}
        onOpenChange={setSiteOpen}
        companyId={id}
        site={editingSite}
        onSaved={(saved) => {
          upsertSite(saved);
          refresh();
        }}
      />
      <EmployeeFormSheet
        open={employeeOpen}
        onOpenChange={setEmployeeOpen}
        employee={null}
        defaultCompanyId={id}
        defaultSiteId={defaultSiteForNewEmployee}
        onSaved={(saved) => {
          upsertEmployee(saved);
          setActiveTab("employees");
          refresh();
        }}
      />
    </div>
  );
}

function DetailCard({ title, children, testId, className }) {
  return (
    <section
      data-testid={testId}
      className={cn(
        "rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]",
        className
      )}
    >
      <header className="border-b border-border px-5 py-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {title}
        </h3>
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function DataGrid({ cols = 2, children }) {
  return (
    <dl
      className={cn(
        "grid gap-x-6 gap-y-3",
        cols === 1 && "grid-cols-1",
        cols === 2 && "grid-cols-1 sm:grid-cols-2"
      )}
    >
      {children}
    </dl>
  );
}

function DataRow({ label, value, icon, mono, multiline }) {
  const isEmpty = value === undefined || value === null || value === "";
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "inline-flex items-center gap-1.5 text-sm",
          isEmpty && "text-muted-foreground/70",
          mono && "font-mono text-[12.5px]",
          multiline && "whitespace-pre-line"
        )}
      >
        {icon && !isEmpty && <span className="text-muted-foreground">{icon}</span>}
        {isEmpty ? "—" : value}
      </dd>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={cn(
        "h-10 whitespace-nowrap px-4 text-left align-middle text-[11px] font-medium uppercase tracking-[0.12em]",
        className
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "", ...rest }) {
  return (
    <td
      className={cn("h-14 whitespace-nowrap px-4 align-middle", className)}
      {...rest}
    >
      {children}
    </td>
  );
}
